import moment from 'moment';
import 'moment-timezone';

const TZ = 'Asia/Taipei';
const DAY_MS = 24 * 60 * 60 * 1000;

export const DEFAULT_PARAMS = {
  maxLookbackDays: 7,
  averageDaysN: 3,
  noiseStdScale: 0.5,
};

function isValidReading(value) {
  if (value === null || value === undefined || value === '') return false;
  return !Number.isNaN(Number(value));
}

function toEpochMs(timestamp) {
  return moment(timestamp).valueOf();
}

function taipeiDayKey(epochMs) {
  return moment.tz(epochMs, TZ).format('YYYY-MM-DD');
}

function taipeiHourMinuteKey(epochMs) {
  return moment.tz(epochMs, TZ).format('HH:mm');
}

function mean(values) {
  if (!values.length) return 0;
  return values.reduce((sum, v) => sum + v, 0) / values.length;
}

function sampleStdDev(values) {
  if (values.length < 2) return 0;
  const avg = mean(values);
  const variance = values.reduce((sum, v) => sum + (v - avg) ** 2, 0) / (values.length - 1);
  return Math.sqrt(variance);
}

function gaussianRandom(meanValue, stddev) {
  let u = 0;
  let v = 0;
  while (u === 0) u = Math.random();
  while (v === 0) v = Math.random();
  const z = Math.sqrt(-2 * Math.log(u)) * Math.cos(2 * Math.PI * v);
  return meanValue + z * stddev;
}

function roundTo(value, decimalPoints) {
  const factor = 10 ** decimalPoints;
  return Math.round(value * factor) / factor;
}

// Binary search over an ascending array of { time } for the last entry strictly before `threshold`.
function lastIndexBefore(sortedByTime, threshold) {
  let lo = 0;
  let hi = sortedByTime.length - 1;
  let result = -1;
  while (lo <= hi) {
    const mid = (lo + hi) >> 1;
    if (sortedByTime[mid].time < threshold) {
      result = mid;
      lo = mid + 1;
    } else {
      hi = mid - 1;
    }
  }
  return result;
}

// Binary search over an ascending array of { time } for the first entry strictly after `threshold`.
function firstIndexAfter(sortedByTime, threshold) {
  let lo = 0;
  let hi = sortedByTime.length - 1;
  let result = -1;
  while (lo <= hi) {
    const mid = (lo + hi) >> 1;
    if (sortedByTime[mid].time > threshold) {
      result = mid;
      hi = mid - 1;
    } else {
      lo = mid + 1;
    }
  }
  return result;
}

function findAnchor(sortedCandidates, startMs) {
  const beforeIdx = lastIndexBefore(sortedCandidates, startMs + 1);
  if (beforeIdx >= 0) return sortedCandidates[beforeIdx];
  const afterIdx = firstIndexAfter(sortedCandidates, startMs - 1);
  return afterIdx >= 0 ? sortedCandidates[afterIdx] : null;
}

// "Real" = a device sample (reading1 present) whose corrected reading2 is also present, since
// only such rows carry a usable value for trend interpolation / day-mean / deviation math below.
function buildRealDataContext(records) {
  const real = records
    .filter(r => isValidReading(r.reading1) && isValidReading(r.reading2))
    .map(r => ({ time: toEpochMs(r.TIMESTAMP), reading2: Number(r.reading2) }))
    .sort((a, b) => a.time - b.time);

  const dayTotals = new Map();
  const dayHourMinuteMap = new Map();

  real.forEach(r => {
    const dayKey = taipeiDayKey(r.time);
    const hm = taipeiHourMinuteKey(r.time);
    const totals = dayTotals.get(dayKey) || { sum: 0, count: 0 };
    totals.sum += r.reading2;
    totals.count += 1;
    dayTotals.set(dayKey, totals);
    dayHourMinuteMap.set(`${dayKey}|${hm}`, r.reading2);
  });

  const dayMeanMap = new Map();
  dayTotals.forEach((totals, dayKey) => dayMeanMap.set(dayKey, totals.sum / totals.count));

  return { real, dayMeanMap, dayHourMinuteMap };
}

/**
 * Widen a user-selected [startDate, endDate] range so the fetched pool of raw records has enough
 * history/future to serve as a reference for trend interpolation and day-of-time-of-day deviations.
 */
export function expandFetchDateRange({ startDate, endDate, offsetDays, maxLookbackDays = DEFAULT_PARAMS.maxLookbackDays }) {
  const fetchStart = moment(startDate).subtract(maxLookbackDays + offsetDays, 'days');
  const fetchEnd = moment(endDate).add(maxLookbackDays, 'days');
  return [fetchStart.toISOString(), fetchEnd.toISOString()];
}

/**
 * Detect missing Output (reading2) points in [startDate, endDate] and compute fill values.
 * `records` is the raw array returned by POST /project/:projectId/output?type=all for a single
 * instrument, fetched over the widened range from `expandFetchDateRange`.
 *
 * Each contiguous run of missing grid points ("block") is filled as: a straight trend line
 * between the real values immediately bounding the block, plus a per-point historical
 * same-clock-time deviation + noise for shape, minus a correction that is linearly ramped across
 * the block so the first filled point exactly matches the real value before the block and the
 * last filled point exactly matches the real value after it (no seam at either edge).
 */
export function computeGapFillPlans(records, options) {
  const {
    startDate,
    endDate,
    intervalMinutes,
    offsetDays,
    maxLookbackDays = DEFAULT_PARAMS.maxLookbackDays,
    averageDaysN = DEFAULT_PARAMS.averageDaysN,
    noiseStdScale = DEFAULT_PARAMS.noiseStdScale,
    decimalPoints = 2,
  } = options;

  const startMs = toEpochMs(startDate);
  const endMs = toEpochMs(endDate);
  const intervalMs = intervalMinutes * 60 * 1000;

  const anchorCandidates = records
    .filter(r => isValidReading(r.reading1))
    .map(r => ({ time: toEpochMs(r.TIMESTAMP) }))
    .sort((a, b) => a.time - b.time);

  if (anchorCandidates.length === 0) {
    return { plans: [], anchor: null };
  }

  const anchor = findAnchor(anchorCandidates, startMs);
  const { real, dayMeanMap, dayHourMinuteMap } = buildRealDataContext(records);

  const recordsByTime = new Map();
  records.forEach(r => recordsByTime.set(toEpochMs(r.TIMESTAMP), r));

  const kMin = Math.ceil((startMs - anchor.time) / intervalMs);
  const kMax = Math.floor((endMs - anchor.time) / intervalMs);

  const grid = [];
  for (let k = kMin; k <= kMax; k += 1) {
    const t = anchor.time + k * intervalMs;
    if (t < startMs || t > endMs) continue;
    const rec = recordsByTime.get(t);
    grid.push({ t, missing: !rec || !isValidReading(rec.reading2) });
  }

  // Historical same-clock-time deviation from that day's mean, used as the "shape" contribution.
  // offsetDays/maxLookbackDays only gate which reference days are eligible here - they no longer
  // affect the trend anchor below, which always uses the nearest real data regardless of age.
  function seasonalAt(t) {
    const hm = taipeiHourMinuteKey(t);
    const deviations = [];
    for (let d = offsetDays; d <= offsetDays + maxLookbackDays; d += 1) {
      const refDayKey = moment.tz(t, TZ).subtract(d, 'days').format('YYYY-MM-DD');
      const key = `${refDayKey}|${hm}`;
      if (dayHourMinuteMap.has(key)) {
        const refDayMean = dayMeanMap.get(refDayKey);
        deviations.push(dayHourMinuteMap.get(key) - refDayMean);
        if (deviations.length >= averageDaysN) break;
      }
    }
    const S = mean(deviations);
    const sigma = sampleStdDev(deviations);
    const noise = sigma > 0 ? gaussianRandom(0, sigma * noiseStdScale) : 0;
    return { S, noise, hasHistory: deviations.length > 0 };
  }

  const plans = [];
  let i = 0;
  while (i < grid.length) {
    if (!grid[i].missing) { i += 1; continue; }

    let j = i;
    while (j < grid.length && grid[j].missing) j += 1;
    const block = grid.slice(i, j);
    i = j;

    const beforeIdx = lastIndexBefore(real, block[0].t);
    const before = beforeIdx >= 0 ? real[beforeIdx] : null;
    const afterIdx = firstIndexAfter(real, block[block.length - 1].t);
    const after = afterIdx >= 0 ? real[afterIdx] : null;

    const trendAt = t => {
      if (before && after) {
        const ratio = (t - before.time) / (after.time - before.time);
        return before.reading2 + ratio * (after.reading2 - before.reading2);
      }
      if (before) return before.reading2;
      if (after) return after.reading2;
      return null;
    };

    const rawPoints = block.map(({ t }) => {
      const trend = trendAt(t);
      const { S, noise, hasHistory } = seasonalAt(t);
      if (trend === null && !hasHistory) return { t, blank: true };
      return { t, blank: false, raw: (trend ?? 0) + S + noise };
    });

    const n = rawPoints.length;
    const first = rawPoints[0];
    const last = rawPoints[n - 1];
    const mismatchHead = before && !first.blank ? first.raw - before.reading2 : 0;
    const mismatchTail = after && !last.blank ? last.raw - after.reading2 : 0;

    rawPoints.forEach((p, x) => {
      if (p.blank) {
        plans.push({ TIMESTAMP: p.t, reading2: '', blank: true });
        return;
      }

      let weightHead = 0;
      let weightTail = 0;
      if (n === 1) {
        if (before) weightHead = 0.5;
        if (after) weightTail = 0.5;
      } else if (before && after) {
        weightHead = (n - 1 - x) / (n - 1);
        weightTail = x / (n - 1);
      } else if (before) {
        weightHead = 1;
      } else if (after) {
        weightTail = 1;
      }

      const correction = weightHead * mismatchHead + weightTail * mismatchTail;
      const value = roundTo(p.raw - correction, decimalPoints);
      plans.push({ TIMESTAMP: p.t, reading2: value, blank: false });
    });
  }

  return { plans, anchor: anchor.time };
}
